import React, { useEffect, useMemo, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

import { useMobile } from "../hooks/custom/useMobile";
import useTab from "../hooks/custom/useTab";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

interface Props {
  open: boolean;
  onClose: () => void;
  path: string;
  isFullUrl?: boolean;
}

export const PdfViwer: React.FC<Props> = ({
  open,
  onClose,
  path,
  isFullUrl,
}) => {
  const mobile = useMobile();
  const tab = useTab();

  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fileUrl = useMemo(() => {
    if (isFullUrl) {
      return path;
    }

    const pdfUrl = `${process.env.REACT_APP_cmsUrl}${path}`;

    return `${process.env.REACT_APP_cmsUrl}/proxy/image?url=${encodeURIComponent(
      pdfUrl,
    )}`;
  }, [path, isFullUrl]);

  /**
   * Lock background scrolling without using position: fixed.
   *
   * position: fixed on body can cause iOS Safari scrolling/layout issues.
   */
  useEffect(() => {
    if (!open) return;

    const html = document.documentElement;
    const body = document.body;

    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
    };
  }, [open]);

  /**
   * Reset PDF state whenever the PDF changes.
   */
  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setError("");
    setNumPages(0);
  }, [open, fileUrl]);

  if (!open) {
    return null;
  }

  const isBottomDrawer = mobile || tab;

  const overlayClass = isBottomDrawer
    ? "pdf-overlay-bottom"
    : "pdf-overlay-end";

  const drawerClass = isBottomDrawer ? "pdf-drawer-bottom" : "pdf-drawer-end";

  const pageWidth = isBottomDrawer
    ? Math.max(window.innerWidth - 24, 280)
    : undefined;

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError("");
  };

  const handleDocumentLoadError = (err: Error) => {
    console.error("Failed to load PDF:", err);

    setLoading(false);
    setError("Unable to load PDF.");
  };

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={`pdf-overlay ${overlayClass}`} onClick={handleOverlayClick}>
      <div className={`pdf-wrapper ${drawerClass}`}>
        <button
          type="button"
          className="close pdf-close"
          aria-label="Close PDF"
          onClick={onClose}
        >
          <span aria-hidden="true">&times;</span>
        </button>

        <div className="pdf-modal">
          <div className="pdf-scroll-container">
            {loading && !error && (
              <div className="pdf-loading">Loading PDF...</div>
            )}

            {error && <div className="pdf-error">{error}</div>}

            {!error && (
              <Document
                file={fileUrl}
                onLoadSuccess={handleDocumentLoadSuccess}
                onLoadError={handleDocumentLoadError}
                loading={null}
                error={null}
              >
                <div className="pdf-pages">
                  {Array.from({ length: numPages }, (_, index) => (
                    <div className="pdf-page-wrapper" key={`page_${index + 1}`}>
                      <Page pageNumber={index + 1} width={pageWidth} />
                    </div>
                  ))}
                </div>
              </Document>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
